import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Card } from "@/components/ui/card";
import { ChatInput } from "@/components/chat/ChatInput";
import { ChatMessages, Attachment } from "@/components/chat/ChatMessages";
import { ChatHeader } from "@/components/chat/ChatHeader";
import { ChatTopBar } from "@/components/chat/ChatTopBar";
import { ConversationsList } from "@/components/chat/ConversationsList";
import { generateAIResponse } from "@/services/aiService";
import { whatsappService } from "@/services/whatsappService";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { History } from "lucide-react";
import { Bot } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Agent } from "@/types/agent";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { v4 as uuidv4 } from 'uuid';

interface Message {
  id: number;
  sender: "user" | "ai";
  text: string;
  timestamp: string;
  attachments?: Attachment[];
}

interface Conversation {
  id: string;
  name: string;
  lastMessage: string;
  timestamp: string;
  unread?: boolean;
  source: "telegram" | "whatsapp";
  messages?: Message[];
}

const Chatbot = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [showConversations, setShowConversations] = useState(!isMobile);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const { currentUser } = useAuth();
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);

  useEffect(() => {
    if (currentUser?.companyId) {
      console.log('Chatbot: Setting company ID from currentUser:', currentUser.companyId);
      setCompanyId(currentUser.companyId);
      setIsLoadingCompany(false);
      return;
    }
    
    const savedCompanyId = localStorage.getItem('currentCompanyId');
    if (savedCompanyId) {
      console.log('Chatbot: Setting company ID from localStorage:', savedCompanyId);
      setCompanyId(savedCompanyId);
      setIsLoadingCompany(false);
    } else {
      console.error('Chatbot: No company ID available from any source');
      setCompanyId(undefined);
      setIsLoadingCompany(false);
    }
  }, [currentUser?.companyId]);

  const { data: agents = [] } = useQuery({
    queryKey: ['agents', companyId],
    queryFn: async () => {
      try {
        if (!companyId) {
          console.log('Chatbot: Skipping agent query - no company ID');
          return [];
        }
        
        console.log('Chatbot: Fetching agents for company:', companyId);
        let query = supabase
          .from('ai_agents')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (currentUser?.type !== 'super_admin' && companyId) {
          query = query.eq('company_id', companyId);
        }
        
        const { data, error } = await query;

        if (error) throw error;

        const formattedAgents = data.map(agent => ({
          ...agent,
          avatar: agent.avatar || `https://api.dicebear.com/7.x/bottts/svg?seed=${agent.name}`,
          icon: Bot,
          id: agent.id,
          name: agent.name,
          description: agent.description,
          model: agent.model,
          apiKey: agent.api_key || "",
          openaiApiKey: agent.openai_api_key || "",
          geminiApiKey: agent.gemini_api_key || "",
          prompt: agent.prompt,
          preferredApi: agent.preferred_api as "mistral" | "openai" | "gemini",
        }));

        return formattedAgents;
      } catch (error) {
        console.error('Erro ao carregar agentes:', error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os agentes.",
          variant: "destructive",
        });
        return [];
      }
    },
    enabled: !!companyId
  });

  useEffect(() => {
    const updateConversations = () => {
      if (!companyId) {
        console.log('Chatbot: No company ID available, skipping conversations update');
        return;
      }
      
      console.log('Chatbot: Updating conversations for company:', companyId);
      const whatsappConversations = whatsappService.getConversations(companyId);
      setConversations(whatsappConversations);
    };

    if (companyId) {
      updateConversations();
      const interval = setInterval(updateConversations, 5000);
      return () => clearInterval(interval);
    }
  }, [companyId]);

  useEffect(() => {
    setShowConversations(!isMobile);
  }, [isMobile]);

  useEffect(() => {
    if (selectedConversation) {
      setMessages(selectedConversation.messages || []);
    }
  }, [selectedConversation]);

  const handleSyncHistory = async () => {
    if (isSyncing || !companyId) return;

    try {
      setIsSyncing(true);
      toast({
        title: "Sincronizando histórico",
        description: "Buscando conversas antigas do WhatsApp...",
      });

      console.log('Chatbot: Syncing conversations for company:', companyId);
      await whatsappService.syncConversations(companyId);
      
      const whatsappConversations = whatsappService.getConversations(companyId);
      setConversations(whatsappConversations);

      toast({
        title: "Sincronização concluída",
        description: "O histórico de conversas foi atualizado com sucesso!",
      });
    } catch (error) {
      console.error('Erro ao sincronizar histórico:', error);
      toast({
        title: "Erro na sincronização",
        description: "Não foi possível buscar o histórico de conversas.",
        variant: "destructive",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSendMessage = async (attachments?: Attachment[]) => {
    if ((!newMessage.trim() && !attachments?.length) || !selectedConversation || !companyId) {
      toast({
        title: "Erro",
        description: !selectedConversation
          ? "Selecione uma conversa primeiro"
          : !companyId
          ? "Empresa não identificada"
          : "Digite uma mensagem ou anexe um arquivo",
        variant: "destructive",
      });
      return;
    }

    const currentTime = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const userMessage: Message = {
      id: Date.now(),
      sender: "user",
      text: newMessage,
      timestamp: currentTime,
      attachments
    };

    setMessages((prev) => [...prev, userMessage]);

    try {
      console.log('Chatbot: Sending message for company:', companyId, 'conversation:', selectedConversation.id);
      
      await supabase.from('chatbot_messages').insert([
        {
          conversation_id: selectedConversation.id,
          content: newMessage,
          direction: 'outgoing',
          processed_by_agent: false,
          company_id: companyId,
          attachments: attachments ? JSON.stringify(attachments) : null
        }
      ]);

      if (newMessage.trim()) {
        await whatsappService.sendMessage(companyId, selectedConversation.id, newMessage);
      }
      
      if (attachments?.length) {
        for (const attachment of attachments) {
          await whatsappService.sendAttachment(
            companyId, 
            selectedConversation.id, 
            attachment
          );
        }
      }

      if (selectedAgent?.apiKey && newMessage.trim()) {
        setIsGenerating(true);
        const aiResponse = await generateAIResponse(newMessage, selectedAgent);

        if (aiResponse) {
          const aiMessage: Message = {
            id: Date.now() + 1,
            sender: "ai",
            text: aiResponse,
            timestamp: new Date().toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };

          setMessages((prev) => [...prev, aiMessage]);

          await supabase.from('chatbot_messages').insert([
            {
              conversation_id: selectedConversation.id,
              content: aiResponse,
              agent_id: selectedAgent.id,
              direction: 'outgoing',
              processed_by_agent: true,
              company_id: companyId
            }
          ]);

          await whatsappService.sendMessage(
            companyId,
            selectedConversation.id, 
            aiResponse
          );
        }
      }
    } catch (error) {
      console.error('Erro ao processar mensagem:', error);
      toast({
        title: "Erro",
        description: "Erro ao processar a mensagem",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
      setNewMessage("");
    }
  };

  const handleConversationSelect = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    if (isMobile) {
      setShowConversations(false);
    }
  };

  const handleAgentSelect = (agent: Agent) => {
    setSelectedAgent(agent);
    toast({
      title: "Agente Selecionado",
      description: `${agent.name} irá processar as próximas mensagens.`,
    });
  };

  useEffect(() => {
    if (!companyId) {
      console.log('Chatbot: No company ID available, skipping message listener setup');
      return;
    }
    
    console.log('Chatbot: Setting up WhatsApp message listener for company:', companyId);
    
    const handleIncomingMessage = async (messageData: any) => {
      console.log('Chatbot: Nova mensagem recebida para empresa', companyId, ':', messageData);
      
      try {
        const conversationId = messageData.from;
        
        if (!selectedConversation || selectedConversation.id !== conversationId) {
          const conversation = conversations.find(c => c.id === conversationId) || {
            id: conversationId,
            name: messageData.notifyName || messageData.from,
            lastMessage: messageData.body,
            timestamp: messageData.timestamp || new Date().toISOString(),
            source: 'whatsapp' as const
          };
          
          setSelectedConversation(conversation);
        }

        const currentTime = new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });

        const incomingMessage: Message = {
          id: Date.now(),
          sender: "user",
          text: messageData.body,
          timestamp: currentTime,
        };

        setMessages((prev) => [...prev, incomingMessage]);

        await supabase.from('chatbot_messages').insert([
          {
            conversation_id: selectedConversation?.id || messageData.from,
            content: messageData.body,
            direction: 'incoming',
            processed_by_agent: false,
            company_id: companyId
          }
        ]);

        if (selectedAgent?.apiKey) {
          console.log('Chatbot: Processando mensagem com agente:', selectedAgent.name);
          const aiResponse = await generateAIResponse(messageData.body, selectedAgent);

          if (aiResponse) {
            console.log('Chatbot: Resposta do agente:', aiResponse);
            
            const aiMessage: Message = {
              id: Date.now() + 1,
              sender: "ai",
              text: aiResponse,
              timestamp: new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              }),
            };

            setMessages((prev) => [...prev, aiMessage]);

            await supabase.from('chatbot_messages').insert([
              {
                conversation_id: selectedConversation?.id || messageData.from,
                content: aiResponse,
                agent_id: selectedAgent.id,
                direction: 'outgoing',
                processed_by_agent: true,
                company_id: companyId
              }
            ]);

            await whatsappService.sendMessage(
              companyId,
              selectedConversation?.id || messageData.from, 
              aiResponse
            );

            console.log('Chatbot: Resposta enviada com sucesso para empresa', companyId);
          }
        }
      } catch (error) {
        console.error('Erro ao processar mensagem automática:', error);
        toast({
          title: "Erro no processamento automático",
          description: "Não foi possível processar a mensagem automaticamente",
          variant: "destructive",
        });
      }
    };

    console.log('Chatbot: Configurando listener de mensagens do WhatsApp para empresa:', companyId);
    whatsappService.onMessage(companyId, handleIncomingMessage);

    return () => {
      console.log('Chatbot: Removendo listener de mensagens do WhatsApp para empresa:', companyId);
      whatsappService.offMessage(companyId, handleIncomingMessage);
    };
  }, [selectedAgent, conversations, companyId, selectedConversation]);

  if (isLoadingCompany) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-2rem)] bg-muted/20">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mb-4"></div>
        <p className="text-muted-foreground">Carregando dados da empresa...</p>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-2rem)] bg-muted/20">
        <div className="text-center max-w-md p-6 bg-card rounded-lg shadow-sm">
          <h2 className="text-2xl font-bold mb-4">Configuração Necessária</h2>
          <p className="text-muted-foreground mb-6">
            Para utilizar o chatbot, é necessário estar vinculado a uma empresa.
            Entre em contato com o administrador do sistema.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-2rem)]">
      <ChatTopBar
        selectedAgent={selectedAgent}
        agents={agents}
        onSelectAgent={handleAgentSelect}
        onToggleConversations={() => setShowConversations(!showConversations)}
        isMobile={isMobile}
      />

      <div className="flex flex-1 overflow-hidden relative bg-[#F0F2F5]">
        <div
          className={`
              ${
                isMobile
                  ? `absolute inset-0 z-10 w-full transform transition-transform duration-300 ease-in-out ${
                      showConversations ? "translate-x-0" : "-translate-x-full"
                    }`
                  : "w-[380px] min-w-[380px] border-r border-[#E9EDEF]"
              } 
              bg-white h-full flex flex-col
            `}
        >
          <div className="p-4 border-b border-[#E9EDEF] flex justify-between items-center">
            <h2 className="text-lg font-semibold">Conversas</h2>
            <div className="flex items-center">
              <div className="text-xs text-green-600 mr-2 font-medium">
                Empresa: {companyId?.slice(0, 8)}...
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncHistory}
                disabled={isSyncing}
              >
                <History className="h-4 w-4 mr-2" />
                {isSyncing ? "Sincronizando..." : "Buscar histórico"}
              </Button>
            </div>
          </div>
          <ConversationsList
            conversations={conversations}
            selectedConversation={selectedConversation}
            onSelectConversation={handleConversationSelect}
          />
        </div>

        {(!isMobile || !showConversations) && (
          <div className="flex-1 flex flex-col min-w-0 bg-[#EFEAE2]">
            <ChatHeader
              selectedAgent={selectedAgent}
              selectedConversation={selectedConversation}
              onBack={isMobile ? () => setShowConversations(true) : undefined}
            />

            <div className="flex-1 overflow-hidden relative">
              <Card className="h-full flex flex-col border-0 rounded-none bg-[#EFEAE2]">
                <ChatMessages messages={messages} />
              </Card>
            </div>

            <div className="p-4 bg-[#F0F2F5] border-t border-[#E9EDEF]">
              <ChatInput
                value={newMessage}
                onChange={setNewMessage}
                onSend={handleSendMessage}
                disabled={isGenerating}
                companyId={companyId}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chatbot;
